import { Group, Stack } from '@mantine/core';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TransformedTarget, TransformedTargetThreshold } from '../../../api/targets-client/targets-client.type';
import { TileFontSize } from '../../../store/dashboards-store/dashboards-store.type';
import { secondaryBase } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import { getTargetComparisonSymbol, getTargetThresholdForValue } from '../../adherence/targets/targets.helpers';
import { TARGET_THRESHOLD_COLORS } from '../../adherence/targets/targets.type';
import { getProratedAverageTotals, getTargetThresholdMessage } from './widget.helpers';

/**
 * Maps the user-selected font size for single-value widgets to a CSS font-size.
 * Design tokens stop at 3rem, so the large option uses an explicit value.
 */
const TILE_VALUE_FONT_SIZES: Record<TileFontSize, string> = {
  [TileFontSize.SMALL]: 'var(--font-size-xlarge)',
  [TileFontSize.MEDIUM]: 'var(--font-size-xxlarge)',
  [TileFontSize.LARGE]: '4rem',
};

interface TileWidgetProps {
  data: {
    average: number;
    total: number;
  };
  selectedTarget: TransformedTarget | null;
  fontSize?: TileFontSize;
  showTotal?: boolean;
}

/**
 * Renders a single-value dashboard widget: the average value centered in the
 * widget body, with optional target threshold indicator and total line.
 *
 * @param {TileWidgetProps['data']} data - Average and total values for the selected measure
 * @param {TransformedTarget | null} selectedTarget - Target whose thresholds color the value, if any
 * @param {TileFontSize} fontSize - User-selected size for the average value (defaults to small)
 * @param {boolean} showTotal - Whether the "Total:" line renders beneath the value (defaults to true)
 */
export const TileWidget: FC<TileWidgetProps> = ({
  data,
  selectedTarget,
  fontSize = TileFontSize.SMALL,
  showTotal = true,
}) => {
  const { t } = useTranslation();
  const { average_lower, average_upper, total_lower, total_upper } = useMemo(
    () =>
      selectedTarget
        ? getProratedAverageTotals(selectedTarget)
        : { average_lower: 0, average_upper: 0, total_lower: 0, total_upper: 0 },
    [selectedTarget],
  );

  const averageThresholds: TransformedTargetThreshold | null = useMemo(() => {
    if (!selectedTarget?.thresholds) {
      return null;
    }

    const thresholdEntries = Object.values(selectedTarget.thresholds);

    if (thresholdEntries.length === 0) {
      return null;
    }

    const calculateAverage = (key: keyof TransformedTargetThreshold) => {
      const values = thresholdEntries
        .map((threshold) => threshold[key])
        .filter((value): value is number => value !== null && value !== undefined);

      const average =
        values.length > 0 ? Math.round((values.reduce((sum, val) => sum + val, 0) / values.length) * 100) / 100 : null;

      return { average };
    };

    return {
      green_threshold_lower: calculateAverage('green_threshold_lower').average,
      green_threshold_upper: calculateAverage('green_threshold_upper').average,
      yellow_threshold_lower: calculateAverage('yellow_threshold_lower').average,
      yellow_threshold_upper: calculateAverage('yellow_threshold_upper').average,
      red_threshold_lower: calculateAverage('red_threshold_lower').average,
      red_threshold_upper: calculateAverage('red_threshold_upper').average,
    };
  }, [selectedTarget]);

  const averageThreshold = averageThresholds && getTargetThresholdForValue(data.average, averageThresholds);

  return (
    <Stack gap="xs" justify="center" align="center" style={{ flex: 1, textAlign: 'center' }}>
      <div>
        <Group gap="xs" justify="center">
          {averageThreshold && (
            <Tooltip
              label={
                selectedTarget && averageThreshold
                  ? getTargetThresholdMessage(averageThresholds, averageThreshold, t)
                  : ''
              }
              variant="black"
              multiline
            >
              <Icon
                name="circle"
                variant="filled"
                color={averageThreshold ? TARGET_THRESHOLD_COLORS[averageThreshold] : secondaryBase}
                size={20}
              />
            </Tooltip>
          )}
          <Text
            weight="bold"
            color={secondaryBase}
            style={{ fontSize: TILE_VALUE_FONT_SIZES[fontSize], lineHeight: 1.15 }}
          >
            {data.average.toLocaleString()}
          </Text>
        </Group>

        {selectedTarget && (
          <Text size="tiny">{`Target: ${getTargetComparisonSymbol(selectedTarget.target_comparison, average_lower, average_upper ?? null)}`}</Text>
        )}
      </div>
      {showTotal && (
        <div>
          <Text size="small" weight="medium">
            Total: {data.total.toLocaleString()}
          </Text>
          {selectedTarget && (
            <Text size="tiny">{`Target: ${getTargetComparisonSymbol(selectedTarget.target_comparison, total_lower, total_upper ?? null)}`}</Text>
          )}
        </div>
      )}
    </Stack>
  );
};
